import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { REWARD_EMAIL_REQUIRED } from './identity/errors'
import { PageResult } from './shared/types'

declare module 'axios' {
  export interface AxiosRequestConfig {
    skip404Interceptor?: boolean
  }

  export interface InternalAxiosRequestConfig {
    skip404Interceptor?: boolean
  }
}

// * this axios instance will be used for authenticated requests
// * with Authorization header defined
export const API: AxiosInstance = axios.create({})

// Create a separate axios instance for TV endpoints that doesn't include credentials
export const TV_API: AxiosInstance = axios.create({
  withCredentials: false,
})

export const CDN_API: AxiosInstance = axios.create()

// Track the current locale for Accept-Language header
let _currentLocale: string
export const initAPI = ({ apiUrl, apiLocale }: { apiUrl: string; apiLocale: string }) => {
  API.defaults.baseURL = apiUrl
  TV_API.defaults.baseURL = apiUrl

  // Include cookies (session) on all requests
  API.defaults.withCredentials = true

  // Set the initial locale
  _currentLocale = apiLocale

  // Add request interceptor to include 'Accept-Language' header with current locale
  API.interceptors.request.use(config => {
    if (config.headers) {
      config.headers['Accept-Language'] = _currentLocale
    }
    return config
  })

  // Add request interceptor to TV_API for locale
  TV_API.interceptors.request.use(config => {
    if (config.headers) {
      config.headers['Accept-Language'] = _currentLocale
    }
    return config
  })

  // Install HTML Content-Type detection at boot so axios rejects WAF block pages (200 +
  // text/html) before they reach callers. TanStack Query treats a resolved promise as
  // success and caches `response.data` — without this, queryFns like listApps would
  // store HTML as data and hooks such as useApps would crash (e.g. `.find` on a string).
  // The noop callback is replaced when AwaitAuth mounts; detection stays active on every route.
  registerIncorrectHtmlContentTypeListener(noopIncorrectHtmlContentTypeListener)
  registerTvIncorrectHtmlContentTypeListener(noopIncorrectHtmlContentTypeListener)
}

// Keep track of interceptor so we can clean it up later
let _interceptor: number | undefined = undefined
export const unauthenticatedListener = (listener: () => void) => {
  if (_interceptor !== undefined) {
    // Clean up previous interceptors
    API.interceptors.response.eject(_interceptor)
  }

  // Register the new listener
  _interceptor = API.interceptors.response.use(undefined, error => {
    if (error.response && error.response.status === 401) listener()
    // Continue with normal error handling
    return Promise.reject(error)
  })
}

// --- 503 Service Unavailable Listener ---
let _503Interceptor: number | undefined = undefined
let _tv503Interceptor: number | undefined = undefined
/**
 * Registers a listener for 503 Service Unavailable responses on both API instances.
 * Cleans up any previous 503 interceptors before registering new ones.
 * @param listener Function to call when a 503 response is received
 */
export const register503Listener = (listener: () => void) => {
  // Clean up previous interceptors
  if (_503Interceptor !== undefined) {
    API.interceptors.response.eject(_503Interceptor)
  }
  if (_tv503Interceptor !== undefined) {
    TV_API.interceptors.response.eject(_tv503Interceptor)
  }

  // Register 503 listener on main API instance
  _503Interceptor = API.interceptors.response.use(undefined, error => {
    if (error.response && error.response.status === 503) listener()
    return Promise.reject(error)
  })

  // Register 503 listener on TV API instance
  _tv503Interceptor = TV_API.interceptors.response.use(undefined, error => {
    if (error.response && error.response.status === 503) listener()
    return Promise.reject(error)
  })
}

let _404Interceptor: number | undefined = undefined
export const register404Listener = (listener: () => void) => {
  // Clean up previous interceptors
  if (_404Interceptor !== undefined) {
    API.interceptors.response.eject(_404Interceptor)
  }

  // Register 404 (Not found) listener on main API instance
  _404Interceptor = API.interceptors.response.use(undefined, error => {
    if (error.response && error.response.status === 404 && !error.config.skip404Interceptor)
      listener()
    return Promise.reject(error)
  })
}

// --- Incorrect HTML Content-Type Listener ---
export const INCORRECT_HTML_CONTENT_TYPE = 'INCORRECT_HTML_CONTENT_TYPE'
export const INCORRECT_HTML_CONTENT_TYPE_MESSAGE = 'API returned text/html instead of JSON'

let _incorrectHtmlContentTypeInterceptor: number | undefined = undefined
let _tvIncorrectHtmlContentTypeInterceptor: number | undefined = undefined

const noopIncorrectHtmlContentTypeListener = () => {
  /* callback supplied later via registerIncorrectHtmlContentTypeListener */
}

const hasIncorrectHtmlContentType = (response: AxiosResponse): boolean => {
  const contentType = response.headers['content-type'] ?? response.headers['Content-Type']
  return typeof contentType === 'string' && contentType.toLowerCase().includes('text/html')
}

const rejectIncorrectHtmlContentType =
  (listener: () => void) =>
  (response: AxiosResponse): Promise<AxiosResponse> => {
    listener()
    return Promise.reject(
      new AxiosError(
        INCORRECT_HTML_CONTENT_TYPE_MESSAGE,
        INCORRECT_HTML_CONTENT_TYPE,
        response.config,
        response.request,
        response,
      ),
    )
  }

/**
 * Registers a listener when an authenticated API response has an incorrect Content-Type
 * (text/html instead of JSON), e.g. WAF block pages that return 200 + text/html.
 *
 * Inspects successful (2xx) responses on API only.
 *
 * Cleans up any previous interceptors before registering new ones.
 * @param listener Function to call when an incorrect text/html Content-Type is received
 */
export const registerIncorrectHtmlContentTypeListener = (listener: () => void) => {
  if (_incorrectHtmlContentTypeInterceptor !== undefined) {
    API.interceptors.response.eject(_incorrectHtmlContentTypeInterceptor)
  }

  const reject = rejectIncorrectHtmlContentType(listener)

  _incorrectHtmlContentTypeInterceptor = API.interceptors.response.use(response => {
    if (hasIncorrectHtmlContentType(response)) {
      return reject(response)
    }
    return response
  })
}

/**
 * Registers a listener when a Kudos TV API response has an incorrect Content-Type
 * (text/html instead of JSON), e.g. WAF block pages that return 200 + text/html.
 *
 * Inspects successful (2xx) responses on TV_API only.
 *
 * Cleans up any previous interceptors before registering new ones.
 * @param listener Function to call when an incorrect text/html Content-Type is received
 */
export const registerTvIncorrectHtmlContentTypeListener = (listener: () => void) => {
  if (_tvIncorrectHtmlContentTypeInterceptor !== undefined) {
    TV_API.interceptors.response.eject(_tvIncorrectHtmlContentTypeInterceptor)
  }

  const reject = rejectIncorrectHtmlContentType(listener)

  _tvIncorrectHtmlContentTypeInterceptor = TV_API.interceptors.response.use(response => {
    if (hasIncorrectHtmlContentType(response)) {
      return reject(response)
    }
    return response
  })
}

// --- RewardEmailRequired Listener ---
let _rewardEmailRequiredInterceptor: number | undefined = undefined
/**
 * Registers a listener for `RewardEmailRequired` responses (deskless members
 * with no email on record). This fires for *any* reward endpoint — store
 * listings, provider lookups, reward details, redemptions — so a single
 * listener can centrally redirect to the rewards email-capture screen instead
 * of each reward surface handling the error individually. See epic SYN-7280.
 *
 * Cleans up any previously registered interceptor before registering a new one.
 * @param listener Function to call when a RewardEmailRequired response is received
 */
export const registerRewardEmailRequiredListener = (listener: () => void) => {
  // Clean up previous interceptor
  if (_rewardEmailRequiredInterceptor !== undefined) {
    API.interceptors.response.eject(_rewardEmailRequiredInterceptor)
  }

  _rewardEmailRequiredInterceptor = API.interceptors.response.use(undefined, error => {
    const data = error.response?.data as { error?: string } | undefined
    if (data?.error === REWARD_EMAIL_REQUIRED) listener()
    return Promise.reject(error)
  })
}

export const unwrapData = <T>(response: AxiosResponse<T>): T => response.data

export const paginateData = <T>(data: T): PageResult<T> => ({
  total: 1,
  results: [data],
  page: 1,
  next: null,
  previous: null,
})

/**
 * Sets the current locale as the Accept-Language header
 * @param locale Language code to use (e.g., 'en', 'es', 'fr')
 */
export const setApiLocale = (locale: string) => {
  _currentLocale = locale
}
